/**
 * Paint Matcher Quiz
 *
 * Recommendations require: (1) indoor/outdoor + surface answers matching
 * Shopify category metafields (suitable space, intended application, and
 * fallbacks from color fields only), and (2) the selected sheen matching at
 * least one product variant (Sheen option or variant text). Finish / sheen
 * is never matched against category metafields. Colour, custom colour, room
 * location, and project size affect cart line properties and variant
 * sizing only — they are not used for metadata scoring.
 */
(function () {
  'use strict';

  var GALLON_COVERAGE = 350;
  var QUART_COVERAGE = 87.5;
  var SAFETY_MARGIN = 1.1;

  var container = document.querySelector('[data-section-type="paint-matcher"]');
  if (!container) return;

  var steps = container.querySelectorAll('[data-pm-step]');
  var progressBar = container.querySelectorAll('[data-pm-progress-step]');
  var btnNext = container.querySelector('[data-pm-next]');
  var btnBack = container.querySelector('[data-pm-back]');
  var resultsEl = container.querySelector('[data-pm-results]');
  var podiumEl = container.querySelector('[data-pm-podium]');
  var restartBtn = container.querySelector('[data-pm-restart]');
  var needSummaryEl = container.querySelector('[data-pm-need-summary]');
  var calcWallsEl = container.querySelector('[data-pm-calc-walls]');
  var calcAddWallBtn = container.querySelector('[data-pm-calc-add-wall]');
  var calcCoatsInput = container.querySelector('[data-pm-calc-coats]');
  var calcErrorEl = container.querySelector('[data-pm-calc-error]');
  var colourSelectEl = container.querySelector('[data-pm-colour-select]');
  var colourFieldWrap = container.querySelector('[data-pm-colour-field]');
  var customColourWrapEl = container.querySelector('[data-pm-custom-colour-wrap]');
  var customColourInput = container.querySelector('[data-pm-custom-colour]');
  var roomInput = container.querySelector('[data-pm-room]');

  var productsScript = container.querySelector('[data-pm-products]');
  var products = [];
  try {
    products = JSON.parse(productsScript.textContent);
  } catch (e) {
    products = [];
  }

  var labelsScript = container.querySelector('[data-pm-labels]');
  var labels = {};
  try {
    labels = JSON.parse(labelsScript.textContent);
  } catch (e) {
    labels = {};
  }

  var currentStep = 0;
  var totalSteps = steps.length;
  var answers = {};
  var wallCounter = 0;
  var calculatorNeeds = null;

  var TAG_SYNONYMS = {
    indoor: ['interior', 'indoors', 'interior walls', 'inside'],
    outdoor: ['exterior', 'outside', 'outdoors', 'exterior walls', 'outdoor space'],
    flat: ['ultra flat', 'ultraflat', 'dead flat'],
    matte: ['matte finish', 'velvet'],
    eggshell: ['low sheen eggshell', 'low sheen - eggshell', 'low sheen eggshell finish'],
    satin: [],
    pearl: ['pearl finish'],
    'low lustre': ['lowlustre'],
    'low sheen': [],
    'semi gloss': ['semi-gloss', 'semigloss', 'semi gloss finish'],
    gloss: ['high gloss', 'high-gloss', 'soft gloss', 'soft-gloss', 'softgloss'],
    'soft gloss': ['soft gloss', 'softgloss'],
    drywall: ['sheetrock', 'gypsum', 'interior walls', 'plasterboard'],
    wood: ['timber', 'doors'],
    brick: ['masonry'],
    ceiling: ['overhead', 'ceilings'],
    metal: ['steel', 'aluminum', 'aluminium']
  };

  var SIZE_SYNONYMS = {
    gallon: ['gallon', 'gallons', 'gal', '1 gallon', 'one gallon', '1 gal'],
    quart: ['quart', 'quarts', 'qt', '1 quart', 'one quart'],
    '5 gallon': ['5 gallon', '5-gallon', '5 gallons', 'five gallon']
  };

  function normalizeText(value) {
    if (value === null || value === undefined) return '';

    return String(value)
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function uniqueArray(items) {
    var seen = {};
    return items.filter(function (item) {
      if (!item) return false;
      if (seen[item]) return false;
      seen[item] = true;
      return true;
    });
  }

  function containsNormalizedPhrase(haystack, phrase) {
    if (!haystack || !phrase) return false;
    return (' ' + haystack + ' ').indexOf(' ' + phrase + ' ') !== -1;
  }

  function flattenMetadataValue(value, output) {
    if (value === null || value === undefined) return;

    if (Array.isArray(value)) {
      value.forEach(function (item) {
        flattenMetadataValue(item, output);
      });
      return;
    }

    if (typeof value === 'object') {
      for (var key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          output.push(key);
          flattenMetadataValue(value[key], output);
        }
      }
      return;
    }

    output.push(String(value));
  }

  function buildTagVariants(tag) {
    var normalized = normalizeText(tag);
    if (!normalized) return [];

    var variants = [normalized];
    var synonymKey = normalized;

    if (normalized.indexOf(' ') !== -1) {
      variants.push(normalized.replace(/\s+/g, '-'));
      variants.push(normalized.replace(/\s+/g, ''));
    }

    if (normalized.indexOf('-') !== -1) {
      synonymKey = normalized.replace(/-/g, ' ');
      variants.push(synonymKey);
      variants.push(synonymKey.replace(/\s+/g, ''));
    }

    (TAG_SYNONYMS[synonymKey] || []).forEach(function (synonym) {
      var normalizedSynonym = normalizeText(synonym);
      if (normalizedSynonym) variants.push(normalizedSynonym);
    });

    return uniqueArray(variants);
  }

  function buildSizeVariants(sizeKey) {
    return uniqueArray((SIZE_SYNONYMS[sizeKey] || []).map(function (term) {
      return normalizeText(term);
    }));
  }

  function matchesAnyVariant(sourceText, variants) {
    for (var i = 0; i < variants.length; i++) {
      if (containsNormalizedPhrase(sourceText, variants[i])) return true;
    }
    return false;
  }

  function metafieldValueToNormalizedText(raw) {
    if (raw === null || raw === undefined || raw === '') return '';
    if (typeof raw === 'string') return normalizeText(raw);
    var parts = [];
    flattenMetadataValue(raw, parts);
    return normalizeText(parts.join(' '));
  }

  function buildProductMatcherData(product) {
    var categoryParts = [];
    var metafields = product && product.metadata ? product.metadata.metafields : null;
    var categoryMetafields = metafields && metafields.shopify ? metafields.shopify : {};

    var suitableRaw = categoryMetafields.suitable_space;
    var intendedRaw = categoryMetafields.intended_application;
    var colorRaw = categoryMetafields.color;
    var colorPatternRaw = categoryMetafields.color_pattern;

    flattenMetadataValue(suitableRaw, categoryParts);
    flattenMetadataValue(intendedRaw, categoryParts);
    flattenMetadataValue(colorRaw, categoryParts);
    flattenMetadataValue(colorPatternRaw, categoryParts);

    function rawMetafieldToDisplayString(raw) {
      if (typeof raw === 'string') return raw;
      var t = [];
      flattenMetadataValue(raw, t);
      return t.join(', ');
    }

    return {
      categoryText: normalizeText(categoryParts.join(' ')),
      suitableSpaceText: metafieldValueToNormalizedText(suitableRaw),
      intendedApplicationText: metafieldValueToNormalizedText(intendedRaw),
      intendedRawString: rawMetafieldToDisplayString(intendedRaw),
      suitableRawString: rawMetafieldToDisplayString(suitableRaw)
    };
  }

  function segmentsFromListRaw(raw) {
    return String(raw || '')
      .split(/\s*,\s*|\s*\|\s*|\s*;\s*|\s+and\s+|\s*\/\s*/i)
      .map(function (x) {
        return normalizeText(x);
      })
      .filter(Boolean);
  }

  function environmentQuizMatchesCategoryMetadata(quizEnvNorm, matcher) {
    if (!quizEnvNorm) return false;
    var variants = buildTagVariants(quizEnvNorm);
    var blob = (matcher.suitableSpaceText + ' ' + matcher.intendedApplicationText)
      .replace(/\s+/g, ' ')
      .trim();
    if (blob) return matchesAnyVariant(blob, variants);
    if (matcher.categoryText) return matchesAnyVariant(matcher.categoryText, variants);
    return false;
  }

  function surfaceMetadataMatch(surfaceTag, m) {
    if (surfaceMatchesCategoryMetadata(surfaceTag, m.intendedRawString)) return true;
    if (!String(m.intendedRawString || '').trim() && m.categoryText) {
      return surfaceMatchesCategoryMetadata(surfaceTag, m.categoryText);
    }
    return false;
  }

  function surfaceMatchesCategoryMetadata(surfaceTag, intendedRawString) {
    var nTag = normalizeText(surfaceTag);
    var raw = String(intendedRawString || '').trim();
    if (!nTag || !raw) return false;
    var segs = segmentsFromListRaw(raw);
    if (!segs.length) segs = [normalizeText(raw)];
    var variants = buildTagVariants(nTag);
    return segs.some(function (seg) {
      return variants.some(function (v) {
        if (seg === v) return true;
        if (v.indexOf(' ') !== -1) return containsNormalizedPhrase(seg, v);
        return seg.split(' ').filter(Boolean).indexOf(v) !== -1;
      });
    });
  }

  function getVariantSearchText(variant) {
    var optionsText = Array.isArray(variant.options) ? variant.options.join(' ') : '';
    return normalizeText((variant.title || '') + ' ' + optionsText);
  }

  function productSheenOptionIndex(product) {
    var opts = product && product.metadata && product.metadata.options;
    if (!Array.isArray(opts)) return -1;
    for (var i = 0; i < opts.length; i++) {
      if (String(opts[i].name || '').toLowerCase() === 'sheen') return i;
    }
    return -1;
  }

  function variantOptionAt(variant, optionIndex) {
    if (!variant || !Array.isArray(variant.options) || optionIndex < 0) return '';
    return String(variant.options[optionIndex] || '');
  }

  function sheenValueMatchesFinishTag(sheenRaw, finishVariants) {
    var sheenNorm = normalizeText(sheenRaw);
    if (!sheenNorm) return false;
    for (var i = 0; i < finishVariants.length; i++) {
      var v = finishVariants[i];
      if (!v) continue;
      if (sheenNorm === v) return true;
      if (v.indexOf(' ') !== -1 && containsNormalizedPhrase(sheenNorm, v)) {
        if (v === 'low sheen' && sheenNorm.indexOf('eggshell') !== -1) continue;
        return true;
      }
    }
    var tokens = sheenNorm.split(' ').filter(Boolean);
    for (var j = 0; j < finishVariants.length; j++) {
      var v2 = finishVariants[j];
      if (!v2 || v2.indexOf(' ') !== -1) continue;
      if (tokens.indexOf(v2) !== -1) return true;
    }
    return false;
  }

  function productHasSheenVariantMatch(product, finishTag) {
    return getFinishMatchedVariants(product, finishTag).length > 0;
  }

  function getFinishMatchedVariants(product, finishTag) {
    var variants = Array.isArray(product.variants) ? product.variants : [];
    if (!variants.length) return [];

    var normalizedFinish = normalizeText(finishTag);
    if (!normalizedFinish) return variants.slice();

    var finishVariants = buildTagVariants(normalizedFinish);
    var sheenIndex = productSheenOptionIndex(product);
    if (sheenIndex < 0) {
      return variants.filter(function (variant) {
        return matchesAnyVariant(getVariantSearchText(variant), finishVariants);
      });
    }

    var matched = variants.filter(function (variant) {
      var sheenVal = variantOptionAt(variant, sheenIndex);
      if (sheenValueMatchesFinishTag(sheenVal, finishVariants)) return true;
      return matchesAnyVariant(getVariantSearchText(variant), finishVariants);
    });
    return matched.length ? matched : [];
  }

  function findSizeVariant(candidates, sizeKey) {
    var sizeVariants = buildSizeVariants(sizeKey);
    var unavailable = null;

    for (var i = 0; i < candidates.length; i++) {
      var variant = candidates[i];
      if (!matchesAnyVariant(getVariantSearchText(variant), sizeVariants)) continue;
      if (variant.available) return variant;
      if (!unavailable) unavailable = variant;
    }

    return unavailable;
  }

  function buildBundleForNeeds(product, finishTag, needs) {
    var finishVariants = getFinishMatchedVariants(product, finishTag);
    if (!finishVariants.length) return null;

    var gallonVariant = needs.gallons > 0 ? findSizeVariant(finishVariants, 'gallon') : null;
    var quartVariant = needs.quarts > 0 ? findSizeVariant(finishVariants, 'quart') : null;

    if ((needs.gallons > 0 && (!gallonVariant || !gallonVariant.available)) ||
        (needs.quarts > 0 && (!quartVariant || !quartVariant.available))) {
      return null;
    }

    var rawItems = [];
    if (needs.gallons > 0) rawItems.push({ id: String(gallonVariant.id), quantity: needs.gallons });
    if (needs.quarts > 0) rawItems.push({ id: String(quartVariant.id), quantity: needs.quarts });

    var mergedItems = rawItems.reduce(function (acc, item) {
      var existing = acc.find(function (entry) { return String(entry.id) === String(item.id); });
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        acc.push(item);
      }
      return acc;
    }, []);

    return {
      gallonVariant: gallonVariant,
      quartVariant: quartVariant,
      displayVariant: gallonVariant || quartVariant || finishVariants[0],
      items: mergedItems
    };
  }

  function getStepAnswer(stepKey, fallbackIndex) {
    var stepsArray = Array.prototype.slice.call(steps);
    var stepIndex = stepsArray.findIndex(function (stepEl) {
      return stepEl.getAttribute('data-pm-step-key') === stepKey;
    });

    if (stepIndex === -1 && typeof fallbackIndex === 'number') stepIndex = fallbackIndex;
    if (stepIndex === -1) return '';

    var selected = answers[stepIndex] || [];
    return selected[0] || '';
  }

  function getStepTagFromDom(stepKey) {
    var stepsArray = Array.prototype.slice.call(steps);
    var stepEl = null;
    for (var i = 0; i < stepsArray.length; i++) {
      if (stepsArray[i].getAttribute('data-pm-step-key') === stepKey) {
        stepEl = stepsArray[i];
        break;
      }
    }
    if (!stepEl) return '';
    var selected = stepEl.querySelectorAll('.paint-matcher__answer.is-selected');
    if (!selected.length) return '';
    return selected[0].getAttribute('data-pm-tag') || '';
  }

  function tagMatchesKeyword(tag, keyword) {
    var normalizedTag = normalizeText(tag);
    if (!normalizedTag) return false;
    return buildTagVariants(keyword).indexOf(normalizedTag) !== -1;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatUnit(count, oneLabel, otherLabel) {
    return count + ' ' + (count === 1 ? oneLabel : otherLabel);
  }

  function formatNeedParts(needs) {
    var parts = [];

    if (needs.gallons > 0) {
      parts.push(formatUnit(
        needs.gallons,
        labels.calc_gallon_one || 'gallon',
        labels.calc_gallon_other || 'gallons'
      ));
    }

    if (needs.quarts > 0) {
      parts.push(formatUnit(
        needs.quarts,
        labels.calc_quart_one || 'quart',
        labels.calc_quart_other || 'quarts'
      ));
    }

    return parts;
  }

  function formatNeedText(needs) {
    var parts = formatNeedParts(needs);
    if (!parts.length) return '';
    return (labels.calc_need_prefix || 'You need') + ' ' + parts.join(' + ');
  }

  function renderNeedSummary(needs) {
    if (!needSummaryEl) return;
    needSummaryEl.textContent = formatNeedText(needs);
  }

  function setCalcError(visible) {
    if (!calcErrorEl) return;
    calcErrorEl.hidden = !visible;
  }

  function escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  function addCalculatorWallRow() {
    if (!calcWallsEl) return;
    wallCounter += 1;

    var wallText = labels.calc_wall || 'Wall';
    var widthText = labels.calc_width || 'Width (ft)';
    var heightText = labels.calc_height || 'Height (ft)';

    var idBase = 'pm-calc-wall-' + wallCounter;
    var idW = idBase + '-w';
    var idH = idBase + '-h';

    var row = document.createElement('div');
    row.className = 'paint-matcher__calc-row';
    row.setAttribute('data-pm-calc-wall', '');
    row.innerHTML =
      '<span class="paint-matcher__calc-row-label">' + wallText + ' ' + wallCounter + '</span>' +
      '<div class="paint-matcher__calc-field">' +
        '<label for="' + idW + '">' + widthText + '</label>' +
        '<input type="number" id="' + idW + '" min="0" step="0.5" inputmode="decimal" data-pm-calc-width aria-label="' + escapeAttr(widthText) + '">' +
      '</div>' +
      '<div class="paint-matcher__calc-field">' +
        '<label for="' + idH + '">' + heightText + '</label>' +
        '<input type="number" id="' + idH + '" min="0" step="0.5" inputmode="decimal" data-pm-calc-height aria-label="' + escapeAttr(heightText) + '">' +
      '</div>' +
      '<button type="button" class="paint-matcher__calc-remove" data-pm-calc-remove-wall aria-label="Remove wall">&times;</button>';

    calcWallsEl.appendChild(row);
    renumberCalculatorWalls();
  }

  function renumberCalculatorWalls() {
    if (!calcWallsEl) return;
    var wallText = labels.calc_wall || 'Wall';
    calcWallsEl.querySelectorAll('[data-pm-calc-wall]').forEach(function (wallEl, i) {
      var labelEl = wallEl.querySelector('.paint-matcher__calc-row-label');
      if (labelEl) labelEl.textContent = wallText + ' ' + (i + 1);
    });
  }

  function calculatePaintNeeds() {
    var coats = parseInt(calcCoatsInput ? calcCoatsInput.value : '2', 10);
    if (isNaN(coats) || coats < 1) coats = 2;

    var totalArea = 0;
    if (!calcWallsEl) return null;

    calcWallsEl.querySelectorAll('[data-pm-calc-wall]').forEach(function (wallEl) {
      var width = parseFloat(wallEl.querySelector('[data-pm-calc-width]').value) || 0;
      var height = parseFloat(wallEl.querySelector('[data-pm-calc-height]').value) || 0;
      totalArea += width * height;
    });

    totalArea = totalArea * coats * SAFETY_MARGIN;
    if (totalArea <= 0) return null;

    var totalQuarts = Math.ceil(totalArea / QUART_COVERAGE);
    var gallons = Math.floor(totalQuarts / 4);
    var quarts = totalQuarts % 4;

    return {
      totalArea: totalArea,
      gallons: gallons,
      quarts: quarts
    };
  }

  function isCurrentStepValid() {
    var stepEl = steps[currentStep];
    var stepKind = stepEl.getAttribute('data-pm-step-kind');

    if (stepKind === 'calculator') {
      var needs = calculatePaintNeeds();
      calculatorNeeds = needs;
      setCalcError(!needs);
      return Boolean(needs);
    }

    if (stepKind === 'colour') {
      return colourSelectEl && colourSelectEl.value.length > 0;
    }

    if (stepKind === 'room') {
      return roomInput && roomInput.value.trim().length > 0;
    }

    var selectedTags = getSelectedTags(currentStep);
    return selectedTags.length > 0;
  }

  products = products.map(function (product) {
    product._matcher = buildProductMatcherData(product);
    return product;
  });

  function populateColourDropdown() {
    if (!colourSelectEl) return;

    $.ajax({
      url: '//api.benjaminmoore.com/api/1d2390152c0df2739cd0/color/GetAllColors',
      data: 'countryCode=en-ca',
      dataType: 'jsonp',
      jsonpCallback: 'PaintMatcherColourCallback',
      error: function () {},
      success: function (response) {
        if (response && response.data && response.data.length) {
          var items = response.data.sort(function (a, b) {
            return a.name.localeCompare(b.name);
          });
          for (var i = 0; i < items.length; i++) {
            var opt = document.createElement('option');
            opt.textContent = items[i].name + ' ' + items[i].number;
            opt.value = items[i].name + ' ' + items[i].number;
            colourSelectEl.appendChild(opt);
          }
        }
        if (typeof $ !== 'undefined' && $.fn && $.fn.select2) {
          $(colourSelectEl).select2({
            dropdownParent: $(colourSelectEl).closest('.paint-matcher__colour-select-wrap'),
            width: '100%'
          });
          $(colourSelectEl).on('select2:select', function () {
            colourSelectEl.dispatchEvent(new Event('change'));
          });
        }
      }
    });
  }

  populateColourDropdown();

  function showStep(index) {
    steps.forEach(function (el, i) {
      el.classList.toggle('is-active', i === index);
    });

    progressBar.forEach(function (el, i) {
      el.classList.toggle('is-active', i === index);
      el.classList.toggle('is-complete', i < index);
    });

    btnBack.hidden = index === 0;
    btnNext.textContent = index === totalSteps - 1
      ? (labels.see_results || 'See results')
      : (labels.next || 'Next');
  }

  function getSelectedTags(stepIndex) {
    var stepEl = steps[stepIndex];
    var selected = stepEl.querySelectorAll('.paint-matcher__answer.is-selected');
    var tags = [];
    selected.forEach(function (el) {
      tags.push(el.getAttribute('data-pm-tag'));
    });
    return tags;
  }

  function saveCurrentAnswers() {
    answers[currentStep] = getSelectedTags(currentStep);
  }

  function scoreProducts(finishTag, surfaceTag, needs, quizEnvironmentTag) {
    var quizEnvNorm = normalizeText(quizEnvironmentTag);
    var finishNorm = normalizeText(finishTag);
    var surfaceNorm = normalizeText(surfaceTag);

    return products.map(function (product) {
      var m = product._matcher;
      var bundle = buildBundleForNeeds(product, finishTag, needs);

      var envOk = environmentQuizMatchesCategoryMetadata(quizEnvNorm, m);
      var surfaceOk = surfaceMetadataMatch(surfaceTag, m);
      var sheenOk = productHasSheenVariantMatch(product, finishTag);
      var fullCategoryMatch = Boolean(
        quizEnvNorm && finishNorm && surfaceNorm && envOk && surfaceOk && sheenOk
      );

      var matchedKeywords = [];
      if (fullCategoryMatch) {
        matchedKeywords = uniqueArray([quizEnvNorm, finishNorm, surfaceNorm]);
      }

      return {
        product: product,
        score: fullCategoryMatch ? 3 : 0,
        matchedKeywords: matchedKeywords,
        bundle: bundle,
        fullCategoryMatch: fullCategoryMatch
      };
    }).sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      var priceA = parseFloat(String(a.product.price_min || a.product.price || '0').replace(/[^0-9.]/g, ''));
      var priceB = parseFloat(String(b.product.price_min || b.product.price || '0').replace(/[^0-9.]/g, ''));
      if (priceB !== priceA) return priceB - priceA;
      return (a.product.title || '').localeCompare(b.product.title || '');
    });
  }

  function bundleHasLineItems(bundle) {
    return bundle && Array.isArray(bundle.items) && bundle.items.length > 0;
  }

  function renderPodium(scored, needs) {
    var topThree = scored.filter(function (item) {
      return item.fullCategoryMatch;
    }).slice(0, 3);

    if (!topThree.length) {
      podiumEl.innerHTML =
        '<div class="paint-matcher__no-results">' +
          '<p class="ff-heading fs-heading-3-base">' + (labels.no_results || 'No matching products found.') + '</p>' +
          '<p class="ff-body fs-body-100">' + (labels.no_results_hint || 'Try different answers or browse our full paint collection.') + '</p>' +
        '</div>';
      return;
    }

    var rankClasses = ['gold', 'silver', 'bronze'];
    var rankLabels = [
      labels.gold || '🥇 Best match',
      labels.silver || '🥈 Runner-up',
      labels.bronze || '🥉 Third place'
    ];
    var medalEmoji = ['🥇', '🥈', '🥉'];
    var needText = formatNeedText(needs);
    var bundleHeading = labels.calc_bundle_heading || 'Exact amount';

    var html = '';
    topThree.forEach(function (item, i) {
      var product = item.product;
      var bundle = item.bundle;
      var productTitle = escapeHtml(product.title);
      var displayVariant = bundle && bundle.displayVariant ? bundle.displayVariant : null;
      if (!displayVariant && product.variants && product.variants[0]) {
        displayVariant = product.variants[0];
      }
      var productPrice = escapeHtml(displayVariant && displayVariant.price ? displayVariant.price : product.price);
      var canAddBundle = bundleHasLineItems(bundle);
      var bundlePayload = canAddBundle ? encodeURIComponent(JSON.stringify(bundle.items)) : '';

      var tagsHtml = '';
      (Array.isArray(product.tags) ? product.tags : []).forEach(function (tag) {
        var isMatch = item.matchedKeywords.some(function (keyword) {
          return tagMatchesKeyword(tag, keyword);
        });
        tagsHtml += '<span class="paint-matcher__tag' + (isMatch ? ' is-match' : '') + '">' + escapeHtml(tag) + '</span>';
      });

      html +=
        '<div class="paint-matcher__podium-item paint-matcher__podium-item--' + rankClasses[i] + '">' +
          '<span class="paint-matcher__medal paint-matcher__medal--' + rankClasses[i] + '">' + medalEmoji[i] + '</span>' +
          '<span class="paint-matcher__podium-rank ff-heading fs-body-100">' + rankLabels[i] + '</span>' +
          (product.image
            ? '<div class="paint-matcher__podium-image"><img src="' + product.image + '" alt="' + productTitle + '" loading="lazy"></div>'
            : '') +
          '<h3 class="paint-matcher__podium-title ff-heading fs-heading-5-base">' + productTitle + '</h3>' +
          '<span class="paint-matcher__podium-price ff-body fs-body-100">' + productPrice + '</span>' +
          '<div class="paint-matcher__podium-tags">' + tagsHtml + '</div>' +
          (needText && canAddBundle
            ? '<div class="paint-matcher__podium-bundle ff-body fs-body-100">' +
                '<strong>' + escapeHtml(bundleHeading) + ':</strong> ' + escapeHtml(needText) +
              '</div>'
            : (needText && !canAddBundle
              ? '<p class="paint-matcher__podium-bundle-hint ff-body fs-body-75 t-subdued">' + escapeHtml(labels.bundle_size_on_product || 'Open the product to choose size and add the amount you calculated above.') + '</p>'
              : '')) +
          '<div class="paint-matcher__podium-actions">' +
            (canAddBundle
              ? '<button type="button" class="btn btn--primary paint-matcher__podium-atc ff-heading" data-pm-add-bundle data-pm-bundle="' + escapeHtml(bundlePayload) + '">' +
                  (labels.add_exact_amount || labels.add_to_cart || 'Add to Cart') +
                '</button>'
              : '') +
            '<a href="' + product.url + '" class="btn btn--secondary paint-matcher__podium-link ff-heading">' + (labels.view_product || 'View product') + '</a>' +
          '</div>' +
        '</div>';
    });

    podiumEl.innerHTML = html;
  }

  function addItemsToCart(items, buttonEl) {
    var defaultButtonText = buttonEl.textContent;
    buttonEl.textContent = labels.adding_to_cart || 'Adding...';
    buttonEl.disabled = true;

    var addedPayload = null;

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (errorPayload) {
          throw new Error((errorPayload && errorPayload.description) || 'Unable to add item to cart.');
        });
      }
      return response.json();
    }).then(function (addResponse) {
      addedPayload = addResponse;
      return fetch('/cart.js', { headers: { Accept: 'application/json' } });
    }).then(function (response) {
      if (!response.ok) throw new Error('Unable to refresh cart state.');
      return response.json();
    }).then(function (cart) {
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: addedPayload } }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));

      if (typeof window.emit === 'function') {
        window.emit('cart:updated', { cart: cart });
        window.emit('quick-cart:updated');
        window.emit('quick-cart:open');
      }
    }).catch(function (error) {
      document.dispatchEvent(new CustomEvent('cart:error', {
        detail: { errorMessage: error.message || 'Unable to add item to cart.' }
      }));
      throw error;
    }).finally(function () {
      if (!buttonEl.hasAttribute('aria-disabled')) buttonEl.disabled = false;
      buttonEl.textContent = defaultButtonText;
    });
  }

  function showResults() {
    saveCurrentAnswers();
    var selectedFinish = getStepTagFromDom('finish') || getStepAnswer('finish', 1);
    var selectedSurface = getStepTagFromDom('surface') || getStepAnswer('surface', 2);
    var quizEnvironment = getStepTagFromDom('environment') || getStepAnswer('environment', 0);
    var scored = scoreProducts(selectedFinish, selectedSurface, calculatorNeeds, quizEnvironment);
    renderNeedSummary(calculatorNeeds);
    renderPodium(scored, calculatorNeeds);

    steps.forEach(function (el) { el.classList.remove('is-active'); });
    container.querySelector('[data-pm-quiz-body]').style.display = 'none';
    resultsEl.classList.add('is-active');
  }

  function ensureCalculatorInitialized() {
    if (!calcWallsEl) return;
    if (!calcWallsEl.querySelector('[data-pm-calc-wall]')) addCalculatorWallRow();
  }

  function getLineItemProperties() {
    var properties = {};

    if (colourSelectEl && colourSelectEl.value) {
      properties['Colour'] = colourSelectEl.value;
    }
    var customVal = customColourInput ? customColourInput.value.trim() : '';
    if (customVal) properties['Custom Colour'] = customVal;

    var roomVal = roomInput ? roomInput.value.trim() : '';
    if (roomVal) properties['Room Location'] = roomVal;

    return properties;
  }

  container.addEventListener('click', function (e) {
    var bundleBtn = e.target.closest('[data-pm-add-bundle]');
    if (bundleBtn) {
      var encodedBundle = bundleBtn.getAttribute('data-pm-bundle');
      if (encodedBundle) {
        try {
          var items = JSON.parse(decodeURIComponent(encodedBundle));
          var properties = getLineItemProperties();
          if (Object.keys(properties).length) {
            items = items.map(function (item) {
              return { id: item.id, quantity: item.quantity, properties: properties };
            });
          }
          addItemsToCart(items, bundleBtn).catch(function () {});
        } catch (err) {
          document.dispatchEvent(new CustomEvent('cart:error', {
            detail: { errorMessage: 'Unable to add selected bundle.' }
          }));
        }
      }
      return;
    }

    if (calcAddWallBtn && e.target.closest('[data-pm-calc-add-wall]')) {
      addCalculatorWallRow();
      return;
    }

    var removeWallBtn = e.target.closest('[data-pm-calc-remove-wall]');
    if (removeWallBtn) {
      var wallRow = removeWallBtn.closest('[data-pm-calc-wall]');
      if (wallRow) wallRow.remove();
      if (calcWallsEl && !calcWallsEl.querySelector('[data-pm-calc-wall]')) addCalculatorWallRow();
      renumberCalculatorWalls();
      return;
    }

    var answer = e.target.closest('.paint-matcher__answer');
    if (!answer) return;

    var step = answer.closest('[data-pm-step]');
    if (!step) return;

    var allowMultiple = step.getAttribute('data-pm-multi') === 'true';
    if (!allowMultiple) {
      step.querySelectorAll('.paint-matcher__answer').forEach(function (el) {
        el.classList.remove('is-selected');
        el.setAttribute('aria-pressed', 'false');
      });
    }

    answer.classList.toggle('is-selected');
    answer.setAttribute('aria-pressed', answer.classList.contains('is-selected') ? 'true' : 'false');
  });

  btnNext.addEventListener('click', function () {
    saveCurrentAnswers();

    if (!isCurrentStepValid()) return;

    if (currentStep < totalSteps - 1) {
      currentStep += 1;
      showStep(currentStep);
    } else {
      showResults();
    }
  });

  btnBack.addEventListener('click', function () {
    if (currentStep > 0) {
      saveCurrentAnswers();
      currentStep -= 1;
      showStep(currentStep);
    }
  });

  if (restartBtn) {
    restartBtn.addEventListener('click', function () {
      currentStep = 0;
      answers = {};
      calculatorNeeds = null;
      setCalcError(false);

      steps.forEach(function (el) {
        el.querySelectorAll('.paint-matcher__answer').forEach(function (answerEl) {
          answerEl.classList.remove('is-selected');
          answerEl.setAttribute('aria-pressed', 'false');
        });
      });

      if (needSummaryEl) needSummaryEl.textContent = '';
      if (calcCoatsInput) calcCoatsInput.value = '2';
      if (calcWallsEl) {
        calcWallsEl.innerHTML = '';
        wallCounter = 0;
        addCalculatorWallRow();
      }
      if (colourSelectEl) {
        if (typeof $ !== 'undefined' && $.fn && $.fn.select2 && $(colourSelectEl).data('select2')) {
          $(colourSelectEl).val('Untinted').trigger('change');
        } else {
          colourSelectEl.selectedIndex = 0;
        }
      }
      if (customColourInput) customColourInput.value = '';
      if (roomInput) roomInput.value = '';

      resultsEl.classList.remove('is-active');
      container.querySelector('[data-pm-quiz-body]').style.display = '';
      showStep(0);
    });
  }

  container.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') {
      var answer = e.target.closest('.paint-matcher__answer');
      if (answer) {
        e.preventDefault();
        answer.click();
      }
    }
  });

  ensureCalculatorInitialized();
  showStep(0);
})();
